import { ClassificationPromptsMoreInfoDialog, RegeneratePromptDialog, ViewPromptDialog } from '@communities/prompt-management/components';
import { useCommunityPrompts, useDeleteCommunityPrompt } from '@communities/prompt-management/hooks';
import { formatPromptVersion } from '@communities/prompt-management/utils';
import { routes as communityRoutes } from '@communities/routes';
import type { TableColumn } from '@core/components';
import { DataList, SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Button, ButtonType, IconButton, IconButtonSize, IconSize, Info16, TestBeaker, xdsVars } from '@soteria/design-system-ve';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PromptsSectionProps {
  communityId: string;
  isCommunityAdmin: boolean;
}

/**
 * PromptsSection Component
 *
 * Self-contained section for managing classification prompts within a community.
 * Displays prompt versions, allows generation, testing, viewing, and deletion.
 */
// eslint-disable-next-line complexity
function PromptsSectionComponent({ communityId, isCommunityAdmin }: PromptsSectionProps) {
  const navigate = useNavigate();

  const { promptData, loading, errors } = useCommunityPrompts(communityId);
  const { deletePrompt, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunityPrompt();

  const [selectedPromptVersion, setSelectedPromptVersion] = useState<string | undefined>(undefined);
  const [isGeneratePromptDialogOpen, setIsGeneratePromptDialogOpen] = useState(false);
  const [isViewPromptDialogOpen, setIsViewPromptDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);

  const handleTestPrompt = (promptVersion: string) => {
    const testPromptUrl = communityRoutes.communities.classificationTesting.withPrompt(communityId, promptVersion);

    navigate(testPromptUrl);
  };

  const isProductionVersion = (promptVersion: string) => promptVersion === promptData?.prodPromptVersion?.classification;

  const columns: TableColumn<string>[] = [
    {
      key: 'version',
      header: 'Version',
      render: promptVersion => promptVersion,
    },
    {
      key: 'creationDate',
      header: 'Created',
      render: promptVersion => formatPromptVersion(promptVersion),
    },
    {
      key: 'status',
      header: 'Status',
      render: promptVersion => {
        const isProdPrompt = isProductionVersion(promptVersion);

        return (
          <span
            style={{
              color: isProdPrompt ? '#28a745' : xdsVars.colors.contentSecondary,
              fontWeight: isProdPrompt ? 'bold' : 'normal',
            }}
          >
            {isProdPrompt ? 'Production' : 'Inactive'}
          </span>
        );
      },
    },
  ];

  const deleteItemContext =
    isCommunityAdmin && communityId
      ? {
          handler: async (item: string) => await deletePrompt(communityId, { promptVersion: item }),
          isDeleting,
          errors: deleteErrors,
          resetErrors,
        }
      : undefined;

  return (
    <>
      <SectionHeader
        description="View and manage safety prompts for content classification."
        endComponent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconButton
              aria-label="Learn more about safety prompts"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
            {isCommunityAdmin && <Button onClick={() => setIsGeneratePromptDialogOpen(true)}>GENERATE PROMPT</Button>}
          </div>
        }
        title={`Safety Prompts (${promptData?.availablePromptVersions?.classification?.length || 0})`}
      />

      <DataList
        additionalAction={
          isCommunityAdmin
            ? {
                icon: TestBeaker,
                label: 'Test',
                onClick: handleTestPrompt,
                buttonType: ButtonType.Primary,
              }
            : undefined
        }
        columns={columns}
        data={[...(promptData?.availablePromptVersions.classification || [])].sort((a, b) => b.localeCompare(a))}
        dataTypeName="prompt"
        deleteItemContext={deleteItemContext}
        error={errors}
        isLoading={loading}
        onView={item => {
          setSelectedPromptVersion(item);
          setIsViewPromptDialogOpen(true);
        }}
      />
      {promptData?.lastModifiedBy && (
        <div style={{ marginTop: '16px', fontSize: '14px', color: xdsVars.colors.contentPrimary }}>
          Last modified: {new Date(promptData?.lastModifiedAt).toLocaleString()}
          {promptData.lastModifiedBy && ` by ${promptData.lastModifiedBy}`}
        </div>
      )}

      {/* View Prompt Dialog */}
      <ViewPromptDialog
        communityId={communityId}
        isCommunityAdmin={isCommunityAdmin}
        isOpen={isViewPromptDialogOpen}
        isProductionPrompt={isProductionVersion(selectedPromptVersion || '')}
        promptVersion={selectedPromptVersion || undefined}
        onClose={() => setIsViewPromptDialogOpen(false)}
      />

      {/* Regenerate Prompt Dialog */}
      <RegeneratePromptDialog communityId={communityId} isOpen={isGeneratePromptDialogOpen} onClose={() => setIsGeneratePromptDialogOpen(false)} />

      {/* More Info Dialog */}
      <ClassificationPromptsMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </>
  );
}

export const PromptsSection = withAppInsightsTracking(PromptsSectionComponent, 'PromptsSection');
